import { AxiosInstance, AxiosResponse } from 'axios';
import { TCart, TCartCount } from '../types/cart';
import { TLocaleAPI, TLocaleISO } from '../types/locale';

// TODO: This was used as response before,
// but the data returned does not match this @Sarith
// type TAddToCart = {
//     recommendationToken: string;
//     results: TAddToCartResult[];
// };
// type TAddToCartResult = {
//     catalogItemID: string;
// };

export type TCartEndpoints = {
    getCart: (dismiss: boolean) => Promise<AxiosResponse<TCart>>;
    getCount: () => Promise<AxiosResponse<TCartCount>>;
    postToCart: (
        offerId: string,
        params: { quantity: number; attachments?: string[] },
        isFouponSelected?: boolean,
    ) => Promise<AxiosResponse<TCartCount>>;
    postAttachments: (offerId: string, attachments: string[]) => Promise<AxiosResponse<TCart>>;
    postFbtItems: (offerCodes: string) => Promise<AxiosResponse<TCart>>;
    postSavedCart: (offerId: string) => Promise<AxiosResponse<TCart>>;
    remove: (offerId: string, cartType: string) => Promise<AxiosResponse<TCart>>;
    postMove: (isoLocale: TLocaleISO, offerId: string, cartType: string, sku: string) => Promise<AxiosResponse<TCart>>;
    put: (offerCode: string, quantity: number) => Promise<AxiosResponse<TCart>>;
    postCoupon: (code: string) => Promise<AxiosResponse<TCart>>;
    deleteCoupon: () => Promise<AxiosResponse<TCart>>;
    postCity: (cityId: number, paramString?: string) => Promise<AxiosResponse<TCart>>;
    postFreebie: (offerCode: string) => Promise<AxiosResponse<TCart>>;
    setFoupon: (offerCode: string, isFouponSelected: boolean) => Promise<AxiosResponse<TCart>>;
};

export default (api: AxiosInstance, apiLocale: TLocaleAPI): TCartEndpoints => {
    // TODO: Why was dismiss removed before? @Sarith
    const getCart = async (dismiss: boolean): Promise<AxiosResponse<TCart>> =>
        api.get(`/_svc/cart-v1/cart${dismiss ? '?dismiss=true' : ''}`, { headers: { 'X-Locale': apiLocale } });

    const getCount = async (): Promise<AxiosResponse<TCartCount>> =>
        api.get<TCartCount>('/_svc/cart-v1/cart/count', { headers: { 'X-Locale': apiLocale } });

    const postToCart = async (
        offerId: string,
        params: { quantity: number; attachments?: string[] } = { quantity: 1 },
        isFouponSelected?: boolean,
    ): Promise<AxiosResponse<TCartCount>> => {
        return api.post<TCartCount>(
            `/_svc/cart-v1/cart/item/${offerId}/${params.quantity}?showCart`,
            {
                offerId,
                ...params,
                isFouponSelected,
            },
            { headers: { 'X-Locale': apiLocale } },
        );
    };

    const postAttachments = async (offerId: string, attachments: string[]): Promise<AxiosResponse<TCart>> => {
        return api.post(
            `/_svc/cart-v1/cart/attach/${offerId}?showCart`,
            {
                attachments,
            },
            { headers: { 'X-Locale': apiLocale } },
        );
    };

    const postFbtItems = async (offerCodes: string): Promise<AxiosResponse<TCart>> => {
        return api.post<TCart>(
            `/_svc/cart-v1/cart/items/${offerCodes}?showCart`,
            {},
            {
                headers: { 'X-Locale': apiLocale },
            },
        );
    };

    const postSavedCart = async (offerId: string): Promise<AxiosResponse<TCart>> => {
        return api.post(
            `/_svc/cart-v1/savedcart/item/${offerId}?showCart`,
            {
                offerId,
            },
            { headers: { 'X-Locale': apiLocale } },
        );
    };

    const remove = async (offerId: string, cartType: string): Promise<AxiosResponse<TCart>> => {
        return api.delete<TCart>(`/_svc/cart-v1/${cartType}/item/${offerId}?showCart`, {
            headers: { 'X-Locale': apiLocale },
        });
    };

    const postMove = async (
        isoLocale: TLocaleISO,
        offerId: string,
        cartType: string,
        sku: string,
    ): Promise<AxiosResponse<TCart>> => {
        switch (cartType) {
            case 'savedcart':
                return api.post<TCart>(
                    `/_svc/cart-v1/cart/move/${offerId}/savedcart?showCart`,
                    {},
                    {
                        headers: { 'X-Locale': apiLocale },
                    },
                );
            case 'cart':
            default:
                return api.post<TCart>(
                    `/_svc/cart-v1/savedcart/move/${offerId}/cart?showCart`,
                    {},
                    {
                        headers: { 'X-Locale': apiLocale },
                    },
                );
        }
    };

    const put = async (offerCode: string, quantity: number): Promise<AxiosResponse<TCart>> =>
        api.put<TCart>(
            `/_svc/cart-v1/cart/item/${offerCode}/${quantity}?showCart`,
            {},
            {
                headers: { 'X-Locale': apiLocale },
            },
        );

    const postCoupon = async (code: string): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>(`/_svc/cart-v1/coupon?code=${code}&showCart`, {}, { headers: { 'X-Locale': apiLocale } });

    const deleteCoupon = async (): Promise<AxiosResponse<TCart>> =>
        api.delete<TCart>(`/_svc/cart-v1/coupon?&showCart`, { headers: { 'X-Locale': apiLocale } });

    const postCity = (cityId: number, paramString = ''): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>(
            `/_svc/cart-v1/destination?id_city=${cityId}${paramString}`,
            {},
            {
                headers: { 'X-Locale': apiLocale },
            },
        );

    const postFreebie = (offerCode: string): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>( // Check type again
            `/_svc/cart-v1/cart/freebie/${offerCode}`, {}, { headers: { 'X-Locale': apiLocale } });

    const setFoupon = (offerCode: string, isFouponSelected: boolean): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>(
            `/_svc/cart-v1/cart/foupon?showCart`,
            { offerCode, isSelected: isFouponSelected },
            { headers: { 'X-Locale': apiLocale } },
        );

    return {
        getCart,
        getCount,
        postToCart,
        postAttachments,
        postFbtItems,
        postSavedCart,
        remove,
        postMove,
        put,
        postCoupon,
        deleteCoupon,
        postCity,
        postFreebie,
        setFoupon,
    };
};
